import { TemplatePreview } from '@Components/Documents';
import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import { useCustomizationOptions, useGetPrintOption, useUploadStaticFile } from '@Hooks/Documents';
import { type ApiPrintOption, type ApiTemplateCustomization, EnumValueType } from '@Model';
import {
    EditFrameMode,
    EditFrameShell,
    HeadlessFrame,
    LoadingFrame,
    MultiFrame,
    replaceInList,
    ScrollContainer,
    useDebouncedCallback,
    useQueuedState,
} from '@synqo/frontend-component-library';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useClientNavigate } from '../../../Routing/useClientNavigate';
import { CustomizationUpdateArgs, CustomizationUpdater } from './Customization';
import { groupCustomizations } from './Customization/customizationGrouping';
import { Customizations } from './Customization/Customizations';
import { useBlobDatasets, useJsonDatasets } from './useDatasets';

interface CustomizeDataProps {
    printOption: ApiPrintOption;
    serverCustomizations: ApiTemplateCustomization[];
    updateCustomization: (c: ApiTemplateCustomization) => Promise<ApiTemplateCustomization>;
    saveCustomizations: (c: ApiTemplateCustomization[]) => Promise<ApiTemplateCustomization[]>;
    reloadCustomizations: () => Promise<ApiTemplateCustomization[] | undefined>;
}

export function FrameCustomizeData() {
    const { itemId: printOptionId } = useParams();
    const printOption = useGetPrintOption(printOptionId ?? '');
    const { customizations, updateCustomization, saveCustomizations, isError, isLoading, refetchCustomizations } =
        useCustomizationOptions(printOptionId);

    if (isLoading || printOption === undefined) {
        return <LoadingFrame />;
    } else if (isError || customizations.length == 0) {
        return <div>Error loading options. Please try again later.</div>;
    }

    return (
        <FrameCustomizeDataBody
            printOption={printOption}
            serverCustomizations={customizations}
            updateCustomization={updateCustomization}
            reloadCustomizations={refetchCustomizations}
            saveCustomizations={saveCustomizations}
        />
    );
}

function FrameCustomizeDataBody({
    printOption,
    serverCustomizations,
    updateCustomization,
    saveCustomizations,
    reloadCustomizations,
}: CustomizeDataProps) {
    const hasPermission = useHasPermission();
    const navigate = useClientNavigate();

    const canEdit = hasPermission(Permission.DocumentsDocumentUpdate);

    const [mode, setMode] = useState(EditFrameMode.View);
    const [isUpdatePending, setIsUpdatePending] = useState(false);
    const { state: customizations, requestSetState, queueDepth } = useQueuedState(serverCustomizations);

    const [jsonPreparedCustomizations, groupedCustomizations] = useMemo(
        () => [Object.entries(Object.groupBy(customizations, (c) => c.dataset)), groupCustomizations(customizations)],
        [customizations],
    );

    const jsonDatasets = useJsonDatasets(jsonPreparedCustomizations);
    const {
        blobs: blobDatasets,
        addToCache: addToBlobCache,
        removeFromCache: removeFromBlobCache,
    } = useBlobDatasets(jsonPreparedCustomizations);
    const { reset: resetPendingFileQueue, processQueueItem, queueFile } = useUploadStaticFile();

    const processUpdatedCustomization = useCallback(
        async (data: ApiTemplateCustomization, newFile?: File) => {
            if (data.type === EnumValueType.Html) {
                return await updateCustomization(data);
            }

            if (data.type === EnumValueType.File) {
                if (newFile !== undefined) {
                    const arrayBuffer = await newFile.arrayBuffer();
                    addToBlobCache(data.value, arrayBuffer);
                    queueFile({
                        fileId: data.value,
                        file: newFile,
                        operation: 'upload',
                    });
                } else {
                    removeFromBlobCache(data.value);
                    queueFile({
                        fileId: data.value,
                        operation: 'delete',
                    });
                    data.value = '';
                }
            }

            return data;
        },
        [updateCustomization, addToBlobCache, removeFromBlobCache, queueFile],
    );

    const stateUpdate = useCallback(
        async (args: CustomizationUpdateArgs) => {
            await requestSetState(async (latest) => {
                const customization = latest.find((c) => c.id === args.id);
                if (customization === undefined) {
                    return false;
                }

                const withUpdatedValue: ApiTemplateCustomization = {
                    ...customization,
                    value: args.value,
                    source: args.source,
                };

                const updated = await processUpdatedCustomization(withUpdatedValue, args.file);

                return replaceInList(latest, updated);
            });

            setIsUpdatePending(false);
        },
        [requestSetState, processUpdatedCustomization],
    );

    const debouncedStateUpdate = useDebouncedCallback<CustomizationUpdateArgs, void, CustomizationUpdater>(
        stateUpdate,
        200,
    );

    const updateCustomizationHandler = useCallback(
        (args: CustomizationUpdateArgs) => {
            setIsUpdatePending(true);
            debouncedStateUpdate(args);
        },
        [setIsUpdatePending, debouncedStateUpdate],
    );

    const resetCustomizations = useCallback(
        () =>
            requestSetState(async () => {
                const reloaded = await reloadCustomizations();
                if (!reloaded) {
                    return false;
                }
                return reloaded;
            }),
        [reloadCustomizations, requestSetState],
    );

    const leaveEditMode = useCallback(() => {
        resetCustomizations();
        resetPendingFileQueue();
        setMode(EditFrameMode.View);
        return true;
    }, [resetCustomizations, resetPendingFileQueue, setMode]);

    const enterEditMode = useCallback(() => {
        leaveEditMode();
        if (!canEdit) {
            return true;
        }

        setMode(EditFrameMode.Edit);
        return true;
    }, [canEdit, leaveEditMode, setMode]);

    const uploadAllFiles = useCallback(
        async (customizations: ApiTemplateCustomization[]) => {
            let uploadResult;
            while ((uploadResult = await processQueueItem())) {
                const localFileId = uploadResult.localId;
                const remoteFileId = uploadResult.file.id;
                const customization = customizations.find(
                    (c) => c.type === EnumValueType.File && c.value === localFileId,
                );
                if (customization !== undefined) {
                    customization.value = remoteFileId;
                }
            }
        },
        [processQueueItem],
    );

    const saveAllCustomizations = useCallback(async () => {
        await requestSetState(async () => {
            await uploadAllFiles(customizations);
            return saveCustomizations(customizations);
        });

        return leaveEditMode();
    }, [requestSetState, customizations, uploadAllFiles, saveCustomizations, leaveEditMode]);

    const resourceLabel = printOption.display_string;

    return (
        <MultiFrame>
            <EditFrameShell
                headerProps={{
                    label: resourceLabel,
                    resourceLabel: `Ausdruck ${resourceLabel} anpassen`,
                    onBack: () => navigate(-1),
                    onAdd: undefined,
                }}
                bannerProps={{
                    mode,
                    loading: queueDepth > 0 || isUpdatePending,
                    enterEditMode,
                    onSave: saveAllCustomizations,
                    onCancel: leaveEditMode,
                }}
            >
                {!!customizations.length && (
                    <Customizations
                        groupedCustomizations={groupedCustomizations}
                        updateData={updateCustomizationHandler}
                        locked={!canEdit || mode !== EditFrameMode.Edit}
                    />
                )}
            </EditFrameShell>
            <HeadlessFrame>
                <ScrollContainer>
                    {customizations.length > 0 && blobDatasets && (
                        <TemplatePreview
                            printOptionId={printOption.id}
                            blobDatasets={blobDatasets}
                            jsonDatasets={jsonDatasets}
                        />
                    )}
                </ScrollContainer>
            </HeadlessFrame>
        </MultiFrame>
    );
}
